import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Menu from 'components/shared/menu';
import useWorkflow from 'hooks/useWorkflow';
import { simpleDate, simpleDateAtTime } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold, H2 } from 'styles/typography';
import BackArrowIcon from 'svg/BackArrowIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';

import CategoryCard from './CategoryCard';
import { INBOUND_REFERRALS_ROUTES, VALUE_NAMES } from './constants';
import ProgressRail from './ProgressRail';
import ReferralModal, { ReferralModalSection } from './ReferralModal';
import { WorkflowStatusBadge } from './StatusBadge';

export default function InboundReferralPage() {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  const { workflow, isLoading, isError, completeAction, isCompletingAction, invokeAction, isInvokingAction } =
    useWorkflow(workflowId);
  // null = closed; otherwise the modal opens in edit mode focused on this section.
  const [editingSection, setEditingSection] = useState<ReferralModalSection | null>(null);

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError || !workflow)
    return (
      <Centered>
        <Body>We couldn’t load this referral.</Body>
      </Centered>
    );

  const patientName = workflow.patientName;
  const dob = simpleDate(workflow.valueFor(VALUE_NAMES.PATIENT_DOB)?.value);
  const buildings = workflow.groups;

  return (
    <Page>
      {editingSection && (
        <ReferralModal workflow={workflow} section={editingSection} onClose={() => setEditingSection(null)} />
      )}
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <BackButton type='button' onClick={() => navigate(INBOUND_REFERRALS_ROUTES.BASE)} aria-label='Back'>
              <BackArrowIcon width={20} height={20} />
            </BackButton>
            <H2>{patientName}</H2>
            <WorkflowStatusBadge status={workflow.status} />
            {workflow.hasOutcome && <OutcomeChip data-testid='workflow-outcome'>{workflow.outcome}</OutcomeChip>}
            {!workflow.isTerminal && (
              <MenuWrap>
                <Menu $size='large'>
                  <Menu.Trigger>
                    <Button
                      variant='outline-gray'
                      style={{ width: 44, height: 44, padding: 0, flexShrink: 0 }}
                      data-testid='referral-actions'>
                      <EllipsisIcon />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ marginTop: 4 }}>
                    <EditMenuItem data-testid='edit-referral' onClick={() => setEditingSection('documents')}>
                      <PencilIcon color='transparent' stroke={colors.black} strokeWidth={1.5} />
                      Edit
                    </EditMenuItem>
                  </Menu.Content>
                </Menu>
              </MenuWrap>
            )}
          </TitleRow>
          {workflow.createdAt && <Subline>Received on {simpleDateAtTime(workflow.createdAt)}</Subline>}
          {(buildings.length > 0 || dob) && (
            <Chips>
              {buildings.map((group) => (
                <Chip key={group.id}>
                  {group.groupType?.displayName ? `${group.groupType.displayName}: ${group.name}` : group.name}
                </Chip>
              ))}
              {dob && <Chip>DOB: {dob}</Chip>}
            </Chips>
          )}
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        <Layout>
          <RailColumn>
            <ProgressRail workflow={workflow} />
          </RailColumn>

          <MainColumn>
            {workflow.actionsByCategory().map(({ category, actions }) => (
              <CategoryCard
                key={category.key}
                category={category}
                actions={actions}
                workflow={workflow}
                onComplete={(actionId, outputs) => completeAction({ actionId, outputs })}
                isCompleting={isCompletingAction}
                onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
                isInvoking={isInvokingAction}
                onEdit={setEditingSection}
              />
            ))}
          </MainColumn>
        </Layout>
      </Page.Content>
    </Page>
  );
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HeaderInner = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  margin-left: auto;
`;

const EditMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.black};
`;

const Subline = styled(BodySmall)`
  color: ${colors.black50};
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled(BodySmall)`
  padding: 4px 12px;
  border-radius: 4px;
  background: ${colors.black05};
  color: ${colors.black75};
`;

const OutcomeChip = styled(BodySmallBold)`
  text-transform: capitalize;
  padding: 3px 12px;
  border-radius: 24px;
  background: ${colors.primaryBlue10};
  color: ${colors.primaryBlue};
`;

const Layout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 300px;
  max-width: 300px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
`;

const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
`;
